// --------------------------------------------------------------
// Created On: 2021-10-04
// Author: Zachary Thomas
//
// Last Modified: 2026-11-02
// Modified By: Zachary Thomas
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useState } from "react";
import GetAssetDocumentResponseBody from "src/types/api/GetAssetDocumentResponseBody";
import getAssetDisplayName from "src/utilities/getAssetDisplayName";
import {
  API,
  PULSE_DEVICE_TYPE,
  SEEKER_C_DEVICE_TYPE,
  SEEKER_SOLAR_DEVICE_TYPE,
  COPILOT_PMG_DEVICE_TYPE,
  COPILOT_DEVICE_TYPE,
  MODBUS_SENSOR_EVS,
} from "../../constants/miscellaneous";
import {
  PULSE_MANUAL,
  COPILOT_INSTALLATION_GUIDE,
  SEEKER_MANUAL,
  PMG_MANUAL,
  VIBRATION_SENSOR_MANUAL,
  EVS_MANUAL,
  DEVICE_CONTROLLER_MANUAL_MAP,
} from "../../constants/userDocumentation";
import { useParams } from "react-router-dom";
import useApi from "../../hooks/useApi";
import Spinner from "../../components/Spinner/Spinner";
import Error500Page from "../Error500Page/Error500Page";
import useReduxSelector from "../../hooks/useReduxSelector";
import Card from "../../components/Card/Card";
import Document from "./Document/Document";

// Page that lists downloadable documents for the current asset.
export default function AssetDocumentsPage(): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [failedToLoad, setFailedToLoad] = useState<boolean>(false);
  const [assetName, setAssetName] = useState<string>("");
  const [assetNickname, setAssetNickname] = useState<string>("");
  const [manuals, setManuals] = useState<Resource[]>([]);
  const [curves, setCurves] = useState<Resource[]>([]);
  const assetDisplayName = getAssetDisplayName(assetName, assetNickname);
  const currentUser = useReduxSelector((state) => state.currentUser);
  const { assetId } = useParams();

  // Get device subscription info.
  useApi(
    () => {
      setLoading(true);
      return true;
    },
    {
      method: "GET",
      url: `${API}/company/${currentUser.companyId}/asset/${assetId}/document`,
    },
    async (response: Response, responseBody: GetAssetDocumentResponseBody) => {
      if (response.ok && responseBody) {
        setAssetName(responseBody.assetName);
        if (responseBody.assetNickname !== undefined) {
          setAssetNickname(responseBody.assetNickname);
        }

        // Add conditional device type manuals.
        const conditionalManuals: Resource[] = [];
        if (responseBody.deviceType === COPILOT_DEVICE_TYPE) {
          conditionalManuals.push(COPILOT_INSTALLATION_GUIDE);
        } else if (responseBody.deviceType === PULSE_DEVICE_TYPE) {
          conditionalManuals.push(PULSE_MANUAL);
        } else if (
          responseBody.deviceType === SEEKER_C_DEVICE_TYPE ||
          responseBody.deviceType === SEEKER_SOLAR_DEVICE_TYPE
        ) {
          conditionalManuals.push(SEEKER_MANUAL);
        } else if (responseBody.deviceType === COPILOT_PMG_DEVICE_TYPE) {
          conditionalManuals.push(PMG_MANUAL);
        }

        // Add conditional Modbus sensor manuals.
        if (responseBody.modbusSensor === MODBUS_SENSOR_EVS) {
          conditionalManuals.push(VIBRATION_SENSOR_MANUAL);
          conditionalManuals.push(EVS_MANUAL);
        }

        // Add conditional monitoring device + controller manuals.
        if (
          responseBody.deviceType !== null &&
          responseBody.controller !== null &&
          Object.hasOwn(DEVICE_CONTROLLER_MANUAL_MAP, responseBody.deviceType || "") &&
          Object.hasOwn(DEVICE_CONTROLLER_MANUAL_MAP[responseBody.deviceType || ""], responseBody.controller || "")
        ) {
          const selectedResource = DEVICE_CONTROLLER_MANUAL_MAP[responseBody.deviceType][responseBody.controller];
          conditionalManuals.push(selectedResource);
        }

        // Since conditional manuals don't have real primary key based IDs,
        // generate new negative unique IDs on retrieval to prevent React key collisions.
        let conditionalResourceId = -1;
        conditionalManuals.forEach((conditionalManual) => {
          conditionalManual.resourceId = conditionalResourceId;
          conditionalResourceId -= 1;
        });

        setManuals([...responseBody.manuals, ...conditionalManuals]);
        setCurves(responseBody.pumpCurves);
        setFailedToLoad(false);
      } else {
        setFailedToLoad(true);
      }
      setLoading(false);
    },
    [assetId, currentUser.companyId]
  );

  return failedToLoad ? (
    <Error500Page />
  ) : (
    <div className="p-4">
      <Spinner loading={loading} />

      {manuals.length > 0 || curves.length > 0 ? (
        <Fragment>
          {manuals.length > 0 && (
            <div className="col-12 col-xl-10 col-xxl-8 offset-xl-1 offset-xxl-2 pb-4">
              <Card title={`${assetDisplayName}'s Manuals`}>
                <div className="p-4">
                  {manuals.map((manual) => (
                    <Document key={manual.resourceId} name={manual.name} endpoint={manual.endpoint} />
                  ))}
                </div>
              </Card>
            </div>
          )}

          {curves.length > 0 && (
            <div className="col-12 col-xl-10 col-xxl-8 offset-xl-1 offset-xxl-2">
              <Card title={`${assetDisplayName}'s Pump Curves`}>
                <div className="p-4">
                  {curves.map((curve) => (
                    <Document key={curve.resourceId} name={curve.name} endpoint={curve.endpoint} />
                  ))}
                </div>
              </Card>
            </div>
          )}
        </Fragment>
      ) : (
        <Card title="Documents">
          <p className="h4 text-center py-5 px-4">
            {!loading && <span>No documents found for {assetDisplayName}.</span>}
          </p>
        </Card>
      )}
    </div>
  );
}

interface Resource {
  resourceId: number;
  name: string;
  endpoint: string;
}
